import React, { useState, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { format, parseISO } from 'date-fns';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';

import { Status, Tipo } from 'utils/variables';

import Card from 'components/Card';
import Button from 'components/Button';
import Pagination from 'components/Pagination';
import Spinner from 'components/Spinner';
import Flag from 'components/Flag';
import Tooltip from 'components/Tooltip';
import Avatar from 'components/Avatar';
import DropDownMenu, { Action } from 'components/DropDownMenu';
import PlayerBaixa from 'components/PlayerBaixa';
import Loader from 'components/ContentLoader/ChamadaAprovacaoLoader';
import { show } from 'store/modules/dialog/actions';
import {
  FiTrash2,
  FiEdit,
  FiChevronRight,
  FiCopy,
  FiPlus,
  FiGlobe,
  FiLink,
  FiPlay,
} from 'react-icons/fi';

import {
  Container,
  ContentCards,
  Image,
  Info,
  Actions,
  AvaliadoresAvatar,
  ModalAvaliacao,
  Form,
} from './styles';
import { Table, ThumbnailContainer } from 'styles/global';
import { Chamada, ChamadaAprovador, ChamadaEmissora } from '../@types';

import notfound from 'assets/semimagem.png';
import TextArea from 'components/TextArea';
import { FormHandles } from '@unform/core';
import api from 'services/api';
import getValidationErrors from 'services/getValidationErros';
import { useModal } from 'hooks/modal';

interface Props {
  loading: boolean;
  pages: number;
  page: number;
  usuarioLogado: { id: number };
  setPage: (page: number) => void;
  handleCopy: (ch: Chamada) => void;
  handleActionSelect: (ch: Chamada, action: Action) => void;
  handleLinkHomeRPC: (ch: Chamada) => void;
  disableEdit: (ch: Chamada) => boolean;
  chamadas: Chamada[];
  setChamadas: React.Dispatch<React.SetStateAction<Chamada[]>>;
}
export default function Aprovacao(props: Props) {
  // react-router-dom
  const navigate = useNavigate();
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [player, setPlayer] = useState({
    open: false,
    midia_url: '',
    pipe: false,
  });

  /**
   * REDUX
   */
  const dispatch = useDispatch();

  const formRefChamada = useRef<FormHandles>(null);
  const selectedId = useRef<number | null>(null);
  const statusChamada = useRef();
  const schemaReprovaChamada = Yup.object().shape({
    observacao: Yup.string().required('Preencha o motivo'),
  });
  const { showModal, hideModal } = useModal();

  /**
   * USE CALLBACK
   */
  const formatDestino = useCallback((chamadaemissoras: ChamadaEmissora[]) => {
    if (chamadaemissoras.length === 8) return 'REDE';
    else return chamadaemissoras.map(e => e.emissora.siglapromo).join(',');
  }, []);

  const showButtons = useCallback(
    (aprovadores: ChamadaAprovador[]) =>
      !aprovadores.find(
        ap =>
          props.usuarioLogado.id &&
          ap.usuario.id === props.usuarioLogado.id &&
          ap.status === 'pendente',
      ),
    [props],
  );

  function copyToClipboard(text: string) {
    const dummy = document.createElement('textarea');
    // to avoid breaking orgain page when copying more words
    // cant copy when adding below this code
    // dummy.style.display = 'none'
    document.body.appendChild(dummy);
    // Be careful if you use texarea. setAttribute('value', value), which works with "input" does not work with "textarea". – Eduard
    dummy.value = text;
    dummy.select();
    document.execCommand('copy');
    document.body.removeChild(dummy);
    toast.success('Link copiado na a área de transferência');
  }
  /**
   * PLAYER
   */
  function handlePlayer(previewurl: string) {
    if (previewurl) {
      setPlayer({
        open: !player.open,
        midia_url: previewurl,
        pipe: false,
      });
    } else {
      dispatch(
        show({
          type: 'warning',
          text: 'Preview indisponível no momento',
          showConfirmButton: false,
        }),
      );
    }
  }

  // fecha player
  function closeVideo() {
    setPlayer({
      open: !player.open,
      midia_url: '',
      pipe: false,
    });
  }

  // finalizaravaliacao é o status final quando tem mais de um aprovador
  const handleSubmitAvaliacao = useCallback(
    async data => {
      if (formRefChamada.current) {
        try {
          formRefChamada.current.setErrors({});
          if (statusChamada.current === Status.ALTERACAO) {
            await schemaReprovaChamada.validate(data, { abortEarly: false });
          }

          const response = await api.put(
            // alterando o status do ChamadaAprovador
            `/chamadaaprovador/${selectedId.current}`,
            statusChamada.current === Status.APROVADO
              ? { status: 'aprovado' }
              : { status: 'reprovado', observacao: data.observacao },
          );
          hideModal();
          if (response && response.data) {
            if (response.data.finalizarAvaliacao === Status.APROVADO) {
              // se o status da ChamadaAprovador forem todos aprovados
              const options = {
                type: 'info',
                contentHTML: `Retranca aprovada por todos avaliadores, você será redirecionado para finalizar as informações do material no kanban`,
                redirectTo: `/chamadas/${selectedId.current}/kanban`,
                timer: 2000,
                showCancelButton: false,
                showConfirmButton: false,
              };
              dispatch(show(options));
            } else if (statusChamada.current === Status.APROVADO) {
              // se o status da Chamada Aprovador for aprovada
              props.setChamadas(prevState =>
                prevState.map(c =>
                  c.id === selectedId.current
                    ? {
                        ...c,
                        chamadaaprovador: c.chamadaaprovador.map(a =>
                          a.usuario.id === response.data.usuario_id
                            ? {
                                ...a,
                                status: response.data.status,
                                observacao: response.data.observacao,
                              }
                            : a,
                        ),
                      }
                    : c,
                ),
              );
              toast.success('Chamada aprovada com sucesso');
            } else {
              // se o status da Chamada for reprovado (se um avaliador já reprova a chamada, independente da avaliação do outro usuário, já cai em alteração)
              props.setChamadas(prevState =>
                prevState.filter(c => c.id !== selectedId.current),
              );
              toast.success('Chamada reprovada com sucesso');
            }
          } else {
            toast.error('Falha de comunicação com o sistema');
          }
        } catch (error) {
          if (error instanceof Yup.ValidationError) {
            const errors = getValidationErrors(error);
            formRefChamada.current.setErrors(errors);
            return;
          }
          if (error instanceof Error) {
            toast.error(error.message);
          }
        }
      }
    },
    [hideModal, schemaReprovaChamada, dispatch, props],
  );

  const handleSubmit = useCallback(
    async (data: any) => {
      setLoadingSubmit(true);
      try {
        await handleSubmitAvaliacao(data);
      } finally {
        setLoadingSubmit(false);
      }
    },
    [handleSubmitAvaliacao],
  );

  const renderAvaliacao = useCallback(
    ch => {
      return (
        <Form ref={formRefChamada} onSubmit={handleSubmit}>
          <ModalAvaliacao>
            <div>
              <h3>{ch.retranca}</h3>
            </div>
            {ch.status === Status.ALTERACAO && (
              <div>
                <TextArea
                  placeholder="Motivo de reprova"
                  name="observacao"
                  height="9rem"
                  disable={false}
                  max={800}
                  errorField=""
                  icon=""
                />
              </div>
            )}
            <div>
              <Button type="submit" color="primary" disabled={loadingSubmit}>
                {!loadingSubmit ? 'CONFIRMAR' : <Spinner type="ThreeDots" />}
              </Button>
            </div>
          </ModalAvaliacao>
        </Form>
      );
    },
    [handleSubmit, loadingSubmit],
  );

  const handleAvaliacao = useCallback(
    ch => {
      selectedId.current = ch.id;
      statusChamada.current = ch.status;

      showModal({
        title: `Confirma ${
          statusChamada.current === Status.ALTERACAO ? 'REPROVA' : 'APROVAÇÃO'
        } de:`,
        component: renderAvaliacao(ch),
        width: '30vw',
      });
    },
    [renderAvaliacao, showModal],
  );

  return (
    <Container>
      <ContentCards>
        <Card>
          <header>
            <h4>LISTA DE CHAMADAS EM APROVAÇÃO</h4>
            <Pagination
              total={props.pages}
              activePage={props.page}
              onClick={page => props.setPage(page)}
            />
            <Button
              color="primary"
              onClick={() => navigate('/chamadas/novo')}
              tooltip="Criar nova chamada"
            >
              NOVA CHAMADA <FiPlus />
            </Button>
          </header>
          {props.loading ? (
            <Loader />
          ) : (
            <div style={{ padding: `0 15px` }}>
              <Table>
                <thead>
                  <tr>
                    <th style={{ width: '230px' }}>PREVIEW</th>
                    <th>DADOS</th>
                    <th style={{ width: '100px' }}>EDITOR</th>
                    <th style={{ width: '240px' }}>AVALIADORES</th>
                    <th style={{ width: '150px', textAlign: 'center' }}>
                      AVALIAÇÃO
                    </th>
                    <th style={{ width: '65px' }}>AÇÕES</th>
                  </tr>
                </thead>
                <tbody>
                  {!!props.chamadas &&
                    props.chamadas.length > 0 &&
                    props.chamadas.map((ch: Chamada, index) => (
                      <tr key={ch.id}>
                        <td>
                          <ThumbnailContainer
                            onClick={() => handlePlayer(ch.midia.preview_url)}
                          >
                            {/* <div> */}
                            <Image
                              key={index}
                              onClick={() => handlePlayer(ch.midia.preview_url)} // () => handlePlayer(ch.midia)}
                              src={ch.midia ? ch.midia.thumbnail_url : ''}
                              loader={<Spinner />}
                              unloader={<img src={notfound} alt="sem thumb" />}
                            />

                            <FiPlay className="icon_play" />
                            {/* </div> */}
                          </ThumbnailContainer>
                        </td>
                        <td>
                          <Info>
                            <ul>
                              <li>
                                <p>RETRANCA:</p>
                                <strong>{ch.retranca}</strong>
                              </li>
                              <li>
                                <p>PROGRAMA:</p>
                                <strong>
                                  {ch.programa ? ch.programa.nome : '----'}
                                </strong>
                              </li>
                              <li>
                                <p>DESTINO:</p>
                                <strong>
                                  {formatDestino(ch.chamadaemissora)}
                                </strong>
                              </li>
                              <li>
                                <p>VEICULAÇÃO:</p>
                                <strong>
                                  {ch.datainicio && ch.datafim
                                    ? `DE ${format(
                                        parseISO(ch.datainicio),
                                        'dd/MM/yyyy',
                                      )} ATÉ ${format(
                                        parseISO(ch.datafim),
                                        'dd/MM/yyyy',
                                      )}`
                                    : '---'}
                                </strong>
                              </li>
                              <li style={{ marginTop: 10 }}>
                                <Flag
                                  type={ch.tipo}
                                  name={ch.tipo}
                                  tooltip="TIPO"
                                />
                                {ch.midia && (
                                  <Flag
                                    name="PREVIEW"
                                    type={ch.midia.statusbaixa}
                                    tooltip="PREVIEW EM BAIXA (COM CLAQUETE)"
                                    handleAction={() =>
                                      handlePlayer(ch.midia.preview_url)
                                    }
                                    icon={
                                      ch.statusbaixa === 'processado' ? null : (
                                        <FiPlay />
                                      )
                                    }
                                    icon2={
                                      ch.statusbaixa === 'processado' ? null : (
                                        <FiLink />
                                      )
                                    }
                                    handleAction2={() =>
                                      copyToClipboard(ch.midia.preview_url)
                                    }
                                    disabledButton={!ch.midia.preview_url}
                                  />
                                )}
                                {![
                                  Tipo.CHAMADA,
                                  Tipo.VINHETA,
                                  Tipo.AFINACAO,
                                  Tipo.CHPATROCINADA,
                                ].includes(ch.tipo) && (
                                  <Flag
                                    name="LINK HOME RPC"
                                    type={
                                      ch.linkhomerpc ? 'cadastrado' : 'inativo'
                                    }
                                    handleAction={() =>
                                      props.handleLinkHomeRPC(ch)
                                    }
                                    icon={<FiGlobe />}
                                  />
                                )}
                              </li>
                            </ul>
                          </Info>
                        </td>
                        <td>
                          <Avatar
                            size="40"
                            name={ch.editor?.nome}
                            src={ch.editor?.avatar_url}
                            round
                          />
                        </td>
                        <td>
                          {!!ch.chamadaaprovador &&
                            ch.chamadaaprovador
                              .filter(
                                chamadaaprovador =>
                                  chamadaaprovador.deleted_at === null,
                              )
                              .map(chamadaaprovador => (
                                <AvaliadoresAvatar
                                  key={chamadaaprovador.usuario.id}
                                  size="40"
                                  name={chamadaaprovador.usuario.nome}
                                  src={chamadaaprovador.usuario.avatar_url}
                                  checked={
                                    chamadaaprovador.status !== 'pendente'
                                  }
                                  round
                                />
                              ))}
                        </td>
                        <td>
                          <Actions $display="column" $flex>
                            {ch.status !== Status.APROVADO && (
                              <>
                                <Button
                                  color="success"
                                  onClick={() =>
                                    handleAvaliacao({
                                      ...ch,
                                      status: Status.APROVADO,
                                    })
                                  }
                                  disabled={showButtons(ch.chamadaaprovador)}
                                >
                                  APROVAR
                                </Button>
                                <Button
                                  color="danger"
                                  onClick={() =>
                                    handleAvaliacao({
                                      ...ch,
                                      status: Status.ALTERACAO,
                                    })
                                  }
                                  disabled={showButtons(ch.chamadaaprovador)}
                                >
                                  REPROVAR
                                </Button>
                              </>
                            )}
                            {ch.status === Status.APROVADO && (
                              <Button
                                color="success"
                                onClick={() =>
                                  navigate(`/chamadas/${ch.id}/kanban`)
                                }
                              >
                                ENVIAR PARA ENG
                              </Button>
                            )}
                          </Actions>
                        </td>
                        <td>
                          <div
                            style={{
                              alignItems: 'center',
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Button
                              id={`id_edit_${ch.id}`}
                              fill
                              color="font"
                              round
                              href={`/chamadas/${ch.id}/kanban`}
                            >
                              <FiChevronRight />
                            </Button>
                            <Tooltip target={`id_edit_${ch.id}`}>
                              IR PARA KANBAN
                            </Tooltip>
                            <Button
                              id={`id_${ch.id}`}
                              fill
                              color="font"
                              round
                              onClick={() => props.handleCopy(ch)}
                            >
                              <FiCopy />
                            </Button>
                            <Tooltip target={`id_${ch.id}`}>DUPLICAR</Tooltip>
                            <DropDownMenu
                              disabled={false}
                              id={`acoes${index}`}
                              actions={[
                                {
                                  value: 0,
                                  label: 'EDITAR',
                                  disabled: props.disableEdit(ch),
                                  icon: <FiEdit />,
                                  href: `/chamadas/${ch.id}`,
                                },
                                {
                                  value: 1,
                                  label: 'APAGAR',
                                  disabled:
                                    ch.status !== Status.EMPROCESSO &&
                                    ch.status !== Status.ALTERACAO,
                                  icon: <FiTrash2 />,
                                },
                              ]}
                              handleClick={(action: Action) =>
                                !action.disabled &&
                                props.handleActionSelect(ch, action)
                              }
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card>
      </ContentCards>
      <PlayerBaixa
        open={player.open}
        toggleModal={() => closeVideo()}
        pipe={player.pipe}
        url={player.midia_url}
      />
    </Container>
  );
}
